// src/i18n/index.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';

/**
 * i18n bootstrap for Scrimly.
 *
 * Supported languages: English (en), Spanish (es).
 *
 * Precedence on first load:
 *   1. The signed-in user's `language` field on their Firestore profile
 *      (applied via `useSyncUserLanguage` once the profile hydrates)
 *   2. localStorage (remembers the toggle choice across sessions before login)
 *   3. Browser navigator language
 *   4. Fallback to 'en'
 *
 * Missing keys fall back to the English string, so partial translations
 * don't blow up the UI. Add new strings to both locale files as you touch
 * screens — don't try to translate the whole app in one pass.
 */

const resources = {
  en: { translation: en },
  es: { translation: es },
};

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: 'en',
      supportedLngs: ['en', 'es'],
      nonExplicitSupportedLngs: true, // "es-MX" → "es"
      load: 'languageOnly',
      interpolation: { escapeValue: false },
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'scrimly.lang',
        caches: ['localStorage'],
      },
      returnEmptyString: false, // empty = fall through to English
    });
}

/**
 * Persist + apply a new UI language.
 * Safe to call with anything — we clamp to supported languages.
 */
export function setAppLanguage(lang) {
  const clean = typeof lang === 'string' && lang.toLowerCase().startsWith('es') ? 'es' : 'en';
  try {
    localStorage.setItem('scrimly.lang', clean);
  } catch {
    // private-mode / Safari quirks — ignore
  }
  if (i18n.language !== clean) {
    i18n.changeLanguage(clean);
  }
  return clean;
}

export default i18n;
